/**
 * AuthBroker - Single-Flight Token Refresh Manager
 *
 * ISSUE_047: Centralized token lifecycle management
 *
 * Key Features:
 * - Single-flight: Only one refresh at a time
 * - Queue waiters: Concurrent requests share the same result
 * - Epoch tracking: Monotonically increasing counter for token versions
 * - Broadcast: Auto-propagates token to Worker + Realtime
 *
 * @module core/auth/AuthBroker
 */

import type { Session } from '@supabase/supabase-js';
import {
  supabase,
  isSupabaseConfigured,
  pingSupabase,
  patchRestClientFetch,
  refreshSessionRaw,
  type RawRefreshResult,
} from '~/shared/lib/supabase';
import { useAuthStore } from '~/core/store/useAuthStore';
import { updateSyncWorkerToken, notifySyncWorkerAuthDead } from '~/core/sync/workerClient';
import { touchQuerySuccess } from '~/core/resilience/HealthMonitor';
import { resilienceReporter } from '~/core/resilience/ResilienceReporter';

// =============================================================================
// TYPES
// =============================================================================

interface Resolver {
  resolve: (value: RefreshResult) => void;
  reject: (error: Error) => void;
}

interface RefreshResult {
  token: string | null;
  epoch: number;
}

export type AuthBrokerEventType =
  | 'token_refreshed'
  | 'refresh_failed'
  | 'auth_dead';

export type AuthBrokerEvent = {
  type: AuthBrokerEventType;
  token?: string | null;
  epoch?: number;
  error?: string;
};

export type AuthBrokerEventHandler = (event: AuthBrokerEvent) => void;

// =============================================================================
// AUTH BROKER
// =============================================================================

type AuthLiveness = 'alive' | 'failing' | 'dead';

class AuthBroker {
  private refreshPromise: Promise<string | null> | null = null;
  private waiters: Array<Resolver> = [];
  private currentEpoch: number = 0;
  private lastRefreshAt: number = 0;
  private eventHandlers: AuthBrokerEventHandler[] = [];
  private isInitialized = false;

  /** Liveness 상태 머신: alive → failing → dead */
  private liveness: AuthLiveness = 'alive';
  private consecutiveFailures = 0;
  private readonly MAX_CONSECUTIVE_FAILURES = 3;

  /** Minimum time between refresh attempts (5 seconds) */
  private readonly MIN_REFRESH_INTERVAL = 5 * 1000;

  /**
   * Token refresh timeout (기본 10초, env 로 override 가능)
   * ISSUE_161: 실험 시 `VITE_AUTH_REFRESH_TIMEOUT_MS=20000` 등으로 조정.
   * 24시간 방치 후 resume 에서 10초 timeout 빈발 → 서버 응답 시간 분포 조사용.
   */
  private readonly REFRESH_TIMEOUT: number =
    Number(import.meta.env['VITE_AUTH_REFRESH_TIMEOUT_MS']) || 10 * 1000;

  /**
   * 타임아웃 직후 externalRefreshResult 도착 race 방어용 polling 창 (기본 2초).
   * ISSUE_161 Phase A 에서 기존 300ms 가 서버 느린 응답(10~12초)을 놓치는
   * 구조적 결함(ε)임을 unit test 로 재현. Phase C-1 에서 2초로 확대해
   * 해당 구간 응답을 잡는다. 시크릿 로그의 "refresh timeout → JWT expired
   * 5회 반복" 패턴 1차 완화.
   *
   * `VITE_AUTH_EXTERNAL_REFRESH_WAIT_MS` 로 오버라이드 가능 (롤백 대비).
   */
  private readonly EXTERNAL_REFRESH_WAIT: number =
    Number(import.meta.env['VITE_AUTH_EXTERNAL_REFRESH_WAIT_MS']) || 2000;

  /** 토큰 만료 임박 시 MIN_REFRESH_INTERVAL throttle을 우회하는 margin (60초) */
  private readonly FORCE_REFRESH_MARGIN_SEC = 60;

  /** SDK 외부 리프레시(TOKEN_REFRESHED)로 받은 토큰 — tryRefresh 타임아웃 시 폴백 */
  private externalRefreshResult: string | null = null;

  /** Minimum time since last refresh before checking on visibility change (60 seconds) */
  private readonly VISIBILITY_CHECK_THRESHOLD = 60 * 1000;

  /** 토큰 만료까지 이 시간 이상 남으면 proactiveRefresh 스킵 (5분) */
  private readonly TOKEN_REFRESH_MARGIN = 5 * 60 * 1000;

  /** onAuthStateChange 구독 해제 함수 */
  private authSubscription: { unsubscribe: () => void } | null = null;

  /** Bound visibility change handler for proper cleanup */
  private boundHandleVisibility = this.handleVisibilityChange.bind(this);

  /**
   * Initialize the AuthBroker
   * Call this once on app startup
   */
  initialize(): void {
    if (this.isInitialized) return;

    if (!isSupabaseConfigured() || !supabase) return;

    this.isInitialized = true;

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        // External refresh (Supabase auto-refresh)
        this.handleExternalRefresh(session.access_token);
      } else if (event === 'SIGNED_IN' && session?.access_token) {
        // ISSUE_161 Phase C-2: raw refresh 성공 후 fire-and-forget 으로 호출한
        // `sb.auth.setSession()` 이 self-triggered SIGNED_IN 을 발화시킨다.
        // 이미 epoch 증가 + broadcast 가 raw path 에서 끝난 상태라 리셋 경로를 타면
        // currentEpoch=1 덮어쓰기 + broadcast 중복으로 Worker 토큰 이벤트 2회 발화.
        // 현재 AuthStore 의 access_token 과 일치하고 epoch 이 이미 진행 중이면
        // self-triggered sync 로 간주하고 no-op.
        const currentToken = useAuthStore.getState().session?.access_token;
        if (this.currentEpoch > 0 && currentToken === session.access_token) {
          return;
        }
        // New sign-in - reset epoch and liveness
        this.currentEpoch = 1;
        this.lastRefreshAt = Date.now(); // SDK가 세션 복원 → proactiveRefresh 중복 방지
        this.consecutiveFailures = 0;
        this.setLiveness('alive');
        this.broadcast(session.access_token);
      } else if (event === 'SIGNED_OUT') {
        // Reset on sign out
        this.currentEpoch = 0;
        this.refreshPromise = null;
        this.waiters = [];
        this.consecutiveFailures = 0;
        this.liveness = 'alive';
      }
    });
    this.authSubscription = subscription;

    // ISSUE_047: Subscribe to visibility changes for proactive token refresh
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.boundHandleVisibility);
    }

    // ISSUE_073: REST fetch 패치 — SDK의 getSession() 파이프라인 완전 우회
    // 매 REST 쿼리마다 SDK가 getSession() → lock → __loadSession() → 만료 시 refresh를 하는데,
    // 이 파이프라인이 백그라운드 복귀 시 교착/지연 유발.
    // AuthStore에서 직접 토큰을 읽어 즉시 반환하도록 교체.
    patchRestClientFetch(() => useAuthStore.getState().session?.access_token ?? null);
  }

  /**
   * Cleanup the AuthBroker (call on app unmount)
   */
  cleanup(): void {
    if (!this.isInitialized) return;

    // F6: onAuthStateChange 구독 해제
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.boundHandleVisibility);
    }

    this.isInitialized = false;
  }

  /**
   * Phase 5-2: Force logout when refresh token is expired
   */
  private async forceLogout(): Promise<void> {
    // 즉시 dead 상태로 전환 — 다른 컴포넌트가 추가 refresh 시도하지 않도록
    this.setLiveness('dead');

    // Worker에 auth_dead 알림 → sync 타이머 즉시 정지
    notifySyncWorkerAuthDead();

    try {
      await supabase?.auth.signOut();
    } catch (err) {
      console.error('[AuthBroker] Error during forced logout:', err);
    }

    // alive→dead race guard: signOut() await 도중 SIGNED_IN이 도착했으면
    // 새 세션 상태를 파괴하지 않고 복귀
    if (this.liveness !== 'dead') {
      return;
    }

    // 상태 리셋
    this.currentEpoch = 0;
    this.refreshPromise = null;
    this.waiters = [];

    // 직접 auth store 정리 — SIGNED_OUT 이벤트에만 의존하면
    // signOut() 실패 시 또는 페이지 리다이렉트가 이벤트 핸들러보다
    // 먼저 실행될 때 localStorage에 profile이 남아 ghost login 발생
    useAuthStore.getState().signOut();

    // 이벤트 emit
    this.emit({
      type: 'refresh_failed',
      error: 'Refresh token expired - session terminated',
    });

    // 로그인 페이지로 리다이렉트
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  /**
   * Handle visibility change - proactive token refresh on tab visibility
   * ISSUE_050: Actually try to refresh when tab becomes visible after long idle
   */
  private handleVisibilityChange(): void {
    if (document.visibilityState !== 'visible') return;

    const delta = Date.now() - this.lastRefreshAt;
    if (delta > this.VISIBILITY_CHECK_THRESHOLD) {
      // Proactive refresh in background (don't await)
      void this.proactiveRefresh();
    }
  }

  /**
   * ISSUE_050: Proactive refresh with SDK health check
   * Called when tab becomes visible after being hidden
   * ISSUE_063: 탭 복귀 직후 브라우저 네트워크 스택 웨이크업 대기 후 시도
   * ISSUE_073: JWT 만료 확인 — 유효하면 refreshSession() 스킵 (navigator.locks 교착 방지)
   */
  private async proactiveRefresh(): Promise<void> {
    try {
      // ISSUE_073: JWT 만료 확인 — AuthStore에서 직접 읽음 (navigator.locks 안 거침)
      // refreshSession()은 navigator.locks를 사용하므로, 불필요한 호출 시
      // 교착 → 메인 스레드 전체 supabase 쿼리 30초 타임아웃 폭주 위험
      const session = useAuthStore.getState().session;
      if (session?.expires_at) {
        const timeUntilExpiry = session.expires_at * 1000 - Date.now();
        if (timeUntilExpiry > this.TOKEN_REFRESH_MARGIN) {
          this.lastRefreshAt = Date.now();
          return;
        }
      }

      // ISSUE_063: 브라우저 네트워크 스택이 안정화될 시간 확보
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (typeof navigator !== 'undefined' && !navigator.onLine) return;

      // 1초 대기 중 SDK가 이미 세션을 복원했을 수 있음 (SIGNED_IN/TOKEN_REFRESHED)
      const deltaAfterWait = Date.now() - this.lastRefreshAt;
      if (deltaAfterWait < this.VISIBILITY_CHECK_THRESHOLD) return;

      // Quick ping to check SDK health
      const pingOk = await pingSupabase();

      if (!pingOk) return;

      // Try to refresh token
      await this.refreshToken();
    } catch (err) {
      console.error('[AuthBroker] Proactive refresh error:', err);
    }
  }

  /**
   * Get current epoch value
   */
  getEpoch(): number {
    return this.currentEpoch;
  }

  /**
   * Request a token refresh
   * Returns the same result for concurrent calls (single-flight pattern)
   */
  async refreshToken(): Promise<RefreshResult> {
    // Auth dead 가드 — 이미 복구 불능 상태면 즉시 반환
    if (this.liveness === 'dead') {
      return { token: null, epoch: this.currentEpoch };
    }

    // ISSUE_063: 오프라인이면 refresh 시도 자체를 스킵 (네트워크 필수 작업)
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const session = this.getCurrentSession();
      return {
        token: session?.access_token || null,
        epoch: this.currentEpoch,
      };
    }

    // Check minimum interval between refreshes
    const timeSinceLastRefresh = Date.now() - this.lastRefreshAt;
    if (timeSinceLastRefresh < this.MIN_REFRESH_INTERVAL) {
      const session = this.getCurrentSession();
      // 토큰 만료 임박(60초 미만)이면 throttle 무시 — 연속 실패 후 재시도 경로에서
      // 만료된 토큰을 반환하면 다음 쿼리도 곧바로 401을 받게 됨.
      const expiresAt = session?.expires_at ?? 0;
      const secondsToExpiry = expiresAt - Math.floor(Date.now() / 1000);
      if (secondsToExpiry > this.FORCE_REFRESH_MARGIN_SEC) {
        return {
          token: session?.access_token || null,
          epoch: this.currentEpoch,
        };
      }
      // fall through: 만료 임박 → 강제 refresh 진행
    }

    // If already refreshing, join the wait queue
    if (this.refreshPromise) {
      return new Promise((resolve, reject) => {
        this.waiters.push({ resolve, reject });
      });
    }

    // Start new refresh
    this.lastRefreshAt = Date.now();

    this.refreshPromise = this.performRefresh();

    try {
      const token = await this.refreshPromise;

      if (token) {
        this.currentEpoch++;
        this.consecutiveFailures = 0;
        this.setLiveness('alive');
        this.broadcast(token);

        // Record success for health monitoring
        touchQuerySuccess();

        // Emit event
        this.emit({
          type: 'token_refreshed',
          token,
          epoch: this.currentEpoch,
        });
      } else {
        // tryRefresh 내부에서 forceLogout()이 이미 dead로 전환했으면
        // consecutiveFailures 카운팅과 liveness 덮어쓰기를 건너뜀
        // (performRefresh가 async로 liveness를 변경할 수 있어 재확인 필요)
        if ((this.liveness as AuthLiveness) !== 'dead') {
          this.consecutiveFailures++;

          if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
            // ISSUE_155: MAX 도달 = 진짜 장애 → reportImmediate (승격)
            resilienceReporter.reportImmediate({
              kind: 'auth_refresh_null',
              detail: `max consecutive failures (${this.consecutiveFailures}) — forcing logout`,
            });
            this.setLiveness('dead');
            void this.forceLogout();
          } else {
            // ISSUE_155: MAX 미만 = transient → report (윈도우 중이면 버퍼)
            resilienceReporter.report({
              kind: 'auth_refresh_null',
              detail: `consecutive failures: ${this.consecutiveFailures}`,
            });
            this.setLiveness('failing');
          }
        }

        this.emit({
          type: 'refresh_failed',
          error: 'No token returned',
        });
      }

      const result: RefreshResult = { token, epoch: this.currentEpoch };

      // Resolve all waiters
      this.waiters.forEach(w => w.resolve(result));
      this.waiters = [];
      this.refreshPromise = null;

      return result;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('[AuthBroker] Refresh failed:', errorMsg);

      this.emit({
        type: 'refresh_failed',
        error: errorMsg,
      });

      // Reject all waiters
      const rejectError = new Error(errorMsg);
      this.waiters.forEach(w => w.reject(rejectError));
      this.waiters = [];
      this.refreshPromise = null;

      // Return current state on failure
      const session = this.getCurrentSession();
      return {
        token: session?.access_token || null,
        epoch: this.currentEpoch,
      };
    }
  }

  /**
   * Register event handler
   */
  onEvent(handler: AuthBrokerEventHandler): () => void {
    this.eventHandlers.push(handler);
    return () => {
      this.eventHandlers = this.eventHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Check if a token is synced (epoch matches)
   * Used by Worker to validate token freshness
   */
  isTokenSynced(epoch: number): boolean {
    return epoch >= this.currentEpoch;
  }

  /**
   * Get the current access token without refreshing
   */
  getCurrentToken(): string | null {
    const session = this.getCurrentSession();
    return session?.access_token || null;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Perform the actual token refresh
   * ISSUE_050: On timeout, try SDK reset + retry before giving up
   */
  private async performRefresh(): Promise<string | null> {
    return this.tryRefresh();
    // SDK reset 제거: resetSupabaseClient()가 두 번째 GoTrueClient를 생성하여
    // navigator.locks 경합을 심화시킴. refreshToken 타임아웃은 일시적 경합이지
    // 데드락이 아니므로, 실패 시 다음 시도(visibility change/TOKEN_REFRESHED)에서 자연 복구.
  }

  /**
   * Single refresh attempt.
   * ISSUE_161 Phase C-2: raw fetch 경로 primary, SDK 는 보조 동기화.
   *
   * 흐름:
   *  1. refreshSessionRaw 호출 (AbortController + 타이밍 계측)
   *  2. 성공 → AuthStore 갱신 + SDK fire-and-forget setSession + 토큰 반환
   *  3. refresh_token 만료 → forceLogout
   *  4. 기타 실패 → EXTERNAL_REFRESH_WAIT polling (SDK autoRefreshTicker race 수거)
   *  5. polling 도 실패 → reporter 보고 + null
   */
  private async tryRefresh(): Promise<string | null> {
    const sb = supabase;
    if (!sb) return null;

    const currentSession = useAuthStore.getState().session;
    const refreshToken = currentSession?.refresh_token;
    if (!refreshToken) return null;

    this.externalRefreshResult = null;
    const startedAt = Date.now();

    // 1. Raw fetch (primary path)
    const raw = await refreshSessionRaw(refreshToken, this.REFRESH_TIMEOUT);

    // 2. 성공 경로
    if (raw.ok && raw.session) {
      const elapsed = Math.round(raw.timing.endedAt - raw.timing.enqueuedAt);
      const dispatchDelay =
        raw.timing.dispatchedAt != null
          ? Math.round(raw.timing.dispatchedAt - raw.timing.enqueuedAt)
          : null;
      const firstByteDelay =
        raw.timing.firstByteAt != null && raw.timing.dispatchedAt != null
          ? Math.round(raw.timing.firstByteAt - raw.timing.dispatchedAt)
          : null;

      if (elapsed > 3000) {
        console.debug(
          `[AuthBroker] Raw refresh success in ${elapsed}ms ` +
            `(enqueue→dispatch=${dispatchDelay ?? 'N/A'}ms, ` +
            `dispatch→firstByte=${firstByteDelay ?? 'N/A'}ms)`,
        );
      }

      // AuthStore 갱신 — 기존 session 의 user 등 필드를 보존하고 토큰만 override
      const baseSession = (currentSession ?? {}) as Partial<Session>;
      const nextSession: Session = {
        ...baseSession,
        access_token: raw.session.access_token,
        refresh_token: raw.session.refresh_token,
        expires_at: raw.session.expires_at,
        expires_in: raw.session.expires_in,
        token_type: raw.session.token_type,
      } as Session;
      useAuthStore.getState().setSession(nextSession);

      // SDK 동기화 — fire-and-forget. 실패해도 primary 는 이미 성공.
      // (SIGNED_IN 이벤트가 self-triggered 로 돌아오지만 onAuthStateChange 핸들러의
      // 재진입 가드가 무시함)
      void sb.auth
        .setSession({
          access_token: raw.session.access_token,
          refresh_token: raw.session.refresh_token,
        })
        .catch((err) =>
          console.debug('[AuthBroker] SDK setSession sync failed (ignored):', err),
        );

      return raw.session.access_token;
    }

    // 3. Refresh token 만료 → 즉시 forceLogout
    if (raw.errorKind === 'refresh_token_expired') {
      console.error('[AuthBroker] Refresh token expired (raw) - forcing logout');
      void this.forceLogout();
      return null;
    }

    // 4. Degraded fallback: EXTERNAL_REFRESH_WAIT polling
    //    SDK autoRefreshTicker 가 병렬로 성공했을 수 있으므로 externalRefreshResult 수거
    const deadline = Date.now() + this.EXTERNAL_REFRESH_WAIT;
    while (Date.now() < deadline) {
      if (this.externalRefreshResult) {
        // ISSUE_100 #28: 일회용 보장 — 소비 즉시 초기화(다음 tryRefresh 가 스테일 토큰 재사용 방지)
        const token = this.externalRefreshResult;
        this.externalRefreshResult = null;
        const elapsed = Date.now() - startedAt;
        console.debug(
          `[AuthBroker] Raw failed (${raw.errorKind}), recovered via external in ${elapsed}ms`,
        );
        return token;
      }
      await new Promise((r) => setTimeout(r, 50));
    }

    // 5. 최종 실패 보고
    const elapsed = Math.round(raw.timing.endedAt - raw.timing.enqueuedAt);
    const dispatchDelay =
      raw.timing.dispatchedAt != null
        ? Math.round(raw.timing.dispatchedAt - raw.timing.enqueuedAt)
        : null;
    const firstByteDelay =
      raw.timing.firstByteAt != null && raw.timing.dispatchedAt != null
        ? Math.round(raw.timing.firstByteAt - raw.timing.dispatchedAt)
        : null;

    resilienceReporter.openWindow('auth_refresh_timeout');
    resilienceReporter.report({
      kind: 'auth_refresh_transient',
      detail: this.formatTimingDetail(raw, elapsed, dispatchDelay, firstByteDelay),
    });
    return null;
  }

  /**
   * ISSUE_161 Phase C-2: Sentry breadcrumb 에 쌓일 detail 포맷.
   * `raw_<kind> total=<N>ms enqueue→dispatch=<N|N/A>ms dispatch→firstByte=<N|N/A>ms status=<N|N/A> limit=<N>ms`
   */
  private formatTimingDetail(
    raw: RawRefreshResult,
    elapsed: number,
    dispatchDelay: number | null,
    firstByteDelay: number | null,
  ): string {
    const kind = raw.errorKind ?? 'unknown';
    const dispatch = dispatchDelay != null ? `${dispatchDelay}ms` : 'N/A';
    const firstByte = firstByteDelay != null ? `${firstByteDelay}ms` : 'N/A';
    const status = raw.status != null ? `${raw.status}` : 'N/A';
    return (
      `raw_${kind} total=${elapsed}ms ` +
      `enqueue→dispatch=${dispatch} dispatch→firstByte=${firstByte} ` +
      `status=${status} limit=${this.REFRESH_TIMEOUT}ms`
    );
  }

  /**
   * Get current session without triggering refresh
   * ISSUE_073: AuthStore에서 직접 읽음 — supabase.auth.getSession()은
   * navigator.locks를 거칠 수 있어 교착 위험
   */
  private getCurrentSession() {
    return useAuthStore.getState().session;
  }

  /**
   * Handle external refresh (Supabase auto-refresh)
   */
  private handleExternalRefresh(token: string): void {
    // tryRefresh()가 lock 대기 중 타임아웃되더라도 이 토큰을 사용할 수 있도록 저장
    this.externalRefreshResult = token;

    // External refresh 성공 → liveness 복원
    this.consecutiveFailures = 0;
    this.setLiveness('alive');

    // lastRefreshAt 갱신 → proactiveRefresh 중복 방지
    this.lastRefreshAt = Date.now();

    // Only increment epoch if not currently refreshing
    if (!this.refreshPromise) {
      this.currentEpoch++;
      this.broadcast(token);

      this.emit({
        type: 'token_refreshed',
        token,
        epoch: this.currentEpoch,
      });
    }
  }

  /**
   * Broadcast token to Worker only
   * ISSUE_073: setAuth는 Coordinator의 doRecovery()에서 순차 실행
   */
  private broadcast(token: string): void {
    // Update Worker
    updateSyncWorkerToken(token, this.currentEpoch);
  }

  /**
   * Liveness 상태 전환 — useAuthStore 동기화 + auth_dead 이벤트 emit
   */
  private setLiveness(newLiveness: AuthLiveness): void {
    if (this.liveness === newLiveness) return;
    this.liveness = newLiveness;
    useAuthStore.getState().setAuthLiveness(newLiveness);
    if (newLiveness === 'dead') {
      this.emit({ type: 'auth_dead' });
    }
  }

  /**
   * Get current liveness state
   */
  getLiveness(): AuthLiveness {
    return this.liveness;
  }

  /**
   * Emit event to handlers
   */
  private emit(event: AuthBrokerEvent): void {
    this.eventHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (err) {
        console.error('[AuthBroker] Event handler error:', err);
      }
    });
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const authBroker = new AuthBroker();

/**
 * Initialize the AuthBroker
 */
export function initializeAuthBroker(): void {
  authBroker.initialize();
}

/**
 * Cleanup the AuthBroker
 */
export function cleanupAuthBroker(): void {
  authBroker.cleanup();
}

/**
 * Refresh token (single-flight)
 */
export function refreshAuthToken(): Promise<RefreshResult> {
  return authBroker.refreshToken();
}

/**
 * Get current epoch
 */
export function getAuthEpoch(): number {
  return authBroker.getEpoch();
}

/**
 * Get current token without refreshing
 */
export function getCurrentAuthToken(): string | null {
  return authBroker.getCurrentToken();
}

/**
 * Check if token epoch is synced
 */
export function isAuthTokenSynced(epoch: number): boolean {
  return authBroker.isTokenSynced(epoch);
}

/**
 * Get auth liveness state
 */
export function getAuthLiveness(): 'alive' | 'failing' | 'dead' {
  return authBroker.getLiveness();
}
